import { memo } from "react";
import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { TeleconsultoriaJoinCallButton } from "../../components/TeleconsultoriaJoinCallButton";
import { InviteTaskActions } from "../../invites/components/InviteTaskActions";
import { readWherebyRoom } from "../../utils/virtualService";
import type { BookedTeleconsultoriaRow } from "../types";

interface BookedTeleconsultoriaRowCardProps {
  row: BookedTeleconsultoriaRow;
  /** Mostra o nome do teleconsultor (listas da clínica). */
  showTeleconsultor?: boolean;
  /** Teleconsultor que aceitou: inicia o próprio atendimento (partOf do host). */
  canStart?: boolean;
  /** Convite ainda não respondido para o usuário logado: aceitar/recusar aqui mesmo. */
  pendingInvite?: fhir5.Task;
}

/** Card compacto: hora grande, paciente, envolvidos e a ação principal à direita. */
export const BookedTeleconsultoriaRowCard = memo(function BookedTeleconsultoriaRowCard({
  row,
  showTeleconsultor = false,
  canStart = false,
  pendingInvite,
}: BookedTeleconsultoriaRowCardProps) {
  const start = row.start ? dayjs(row.start) : undefined;
  const details = [
    showTeleconsultor ? `Teleconsultor: ${row.teleconsultorName}` : "",
    row.requesterNames.length ? `Solicitante: ${row.requesterNames.join(", ")}` : "",
  ].filter(Boolean);
  const isAwaitingMe = !canStart && Boolean(pendingInvite);

  return (
    <Paper variant="outlined" role="listitem" sx={{ p: 1.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ sm: "center" }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ minWidth: 64, textAlign: "center", flexShrink: 0 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {start?.isValid() ? start.format("HH:mm") : "--:--"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {start?.isValid() ? start.format("DD/MM") : "Sem data"}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {row.patientName}
            </Typography>
            {details.map((detail) => (
              <Typography key={detail} variant="body2" color="text.secondary" noWrap>
                {detail}
              </Typography>
            ))}
            {isAwaitingMe && (
              <Chip size="small" color="warning" variant="outlined" label="Aguardando seu aceite" sx={{ alignSelf: "flex-start" }} />
            )}
          </Stack>
        </Stack>
        {/* Ainda não aceitou: aceitar inclui você na consulta e libera o "Iniciar". */}
        {isAwaitingMe && pendingInvite && (
          <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
            <InviteTaskActions task={pendingInvite} />
          </Box>
        )}
        {canStart && (
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
              "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
            }}
          >
            <TeleconsultoriaJoinCallButton
              roomUrl={readWherebyRoom(row.appointment.virtualService)?.addressUrl || ""}
              appointmentId={row.appointmentId}
              patientName={row.patientName}
              appointment={row.appointment}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  );
});
